import AxiosInstance from '../../helpers/axios-instance/axios-instance';
import { appConfig } from '../../helpers/app-config/app-config';
import type {
  BulkAssignPayload,
  BulkOperationResult,
  BulkRemovePayload,
  GroupMember,
  OrganizationUser,
  PermissionGroup,
} from './rbac-client.type';

const axiosInstance = AxiosInstance();
const baseURL = appConfig.VITE_APP_API_URL + '/api/rbac/permission-groups/';

/**
 * Fetches all permission groups for an organization
 *
 * @param {string} organizationId The organization ID
 * @returns {Promise<{ results: PermissionGroup[] }>} The list of permission groups
 */
function getPermissionGroups(organizationId: string): Promise<{ results: PermissionGroup[] }> {
  const params = new URLSearchParams({ organization_id: organizationId });
  return axiosInstance
    .get(`${baseURL}?${params.toString()}`)
    .then((response) => response.data)
    .catch((error) => Promise.reject(error.response));
}

/**
 * Fetches members of a specific permission group
 *
 * @param {string} groupId The permission group ID
 * @returns {Promise<{ results: GroupMember[] }>} The list of group members
 */
function getGroupMembers(groupId: string): Promise<{ results: GroupMember[] }> {
  return axiosInstance
    .get(`${baseURL}${groupId}/members/`)
    .then((response) => response.data)
    .catch((error) => Promise.reject(error.response));
}

/**
 * Fetches all users in an organization
 *
 * @param {string} organizationId The organization ID
 * @returns {Promise<{ results: OrganizationUser[] }>} The list of organization users
 */
function getOrganizationUsers(organizationId: string): Promise<{ results: OrganizationUser[] }> {
  const params = new URLSearchParams({ organization_id: organizationId });
  return axiosInstance
    .get(`${baseURL}org-users/?${params.toString()}`)
    .then((response) => response.data)
    .catch((error) => Promise.reject(error.response));
}

/**
 * Assigns users to a permission group
 *
 * @param {BulkAssignPayload} payload The assign payload
 * @returns {Promise<BulkOperationResult>} The operation result
 */
function assignUsersToGroup(payload: BulkAssignPayload): Promise<BulkOperationResult> {
  return axiosInstance
    .post(`${baseURL}assign/`, payload)
    .then((response) => response.data)
    .catch((error) => Promise.reject(error.response));
}

/**
 * Removes users from a permission group
 *
 * @param {BulkRemovePayload} payload The remove payload
 * @returns {Promise<BulkOperationResult>} The operation result
 */
function removeUsersFromGroup(payload: BulkRemovePayload): Promise<BulkOperationResult> {
  return axiosInstance
    .post(`${baseURL}remove/`, payload)
    .then((response) => response.data)
    .catch((error) => Promise.reject(error.response));
}

export { assignUsersToGroup, getGroupMembers, getOrganizationUsers, getPermissionGroups, removeUsersFromGroup };
